import { useCallback, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import {
  ArrowLeft,
  Check,
  Cloud,
  Loader2,
  Lock,
  MoreHorizontal,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmptyState } from "@/components/shared/empty-state";
import { aggregateSaveStatus, type SaveStatus } from "@/hooks/use-autosave";
import { formatApiError } from "@/lib/format-api-error";
import { useForm } from "./use-form";
import { FormStatusBadge } from "./form-status-badge";
import { FormBuilderCanvas } from "./form-builder-canvas";
import { WorkflowConfigForm } from "./workflow-config-form";
import { AutomationConfigForm } from "./automation-config-form";
import { ShareFormDialog } from "./share-form-dialog";
import { usePublishForm } from "./use-publish-form";
import { useUnpublishForm } from "./use-unpublish-form";
import { useArchiveForm } from "./use-archive-form";
import { useDeleteForm } from "./use-delete-form";

// Form builder shell — orchestrates the 3 tabs (build / automation /
// workflow), the header (title + lock + status + save indicator + actions),
// and the action menu (publish / unpublish / archive / restore / share /
// delete). Reads the form via useForm(id) → adminForms.detail(id); the
// per-field hooks all do optimistic cache writes against the same key so
// the canvas re-renders from cache automatically.
//
// SaveStatus aggregation: each of the 3 tab content components reports
// its local SaveStatus via callback; the page maintains 3 SaveStatus
// pieces of state and aggregates them for the header indicator with
// precedence: error > saving > saved > idle.

type Tab = "build" | "automation" | "workflow";
const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: "build", label: "Form builder" },
  { key: "automation", label: "Automation" },
  { key: "workflow", label: "Workflow" },
];

function normalizeTab(raw: string | null): Tab {
  if (raw === "build" || raw === "automation" || raw === "workflow") {
    return raw;
  }
  return "build";
}

export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get("tab"));

  if (!id) return <Navigate to="/admin/forms" replace />;

  return (
    <FormBuilderInner
      formId={id}
      tab={tab}
      onTabChange={(next) => {
        const sp = new URLSearchParams(searchParams);
        sp.set("tab", next);
        setSearchParams(sp, { replace: true });
      }}
    />
  );
}

function FormBuilderInner({
  formId,
  tab,
  onTabChange,
}: {
  formId: string;
  tab: Tab;
  onTabChange: (next: Tab) => void;
}) {
  const query = useForm(formId);
  const [defStatus, setDefStatus] = useState<SaveStatus>("idle");
  const [wfStatus, setWfStatus] = useState<SaveStatus>("idle");
  const [autoStatus, setAutoStatus] = useState<SaveStatus>("idle");
  const [shareOpen, setShareOpen] = useState(false);

  // Stable callbacks so child useEffects don't refire on every parent
  // render.
  const handleDefStatus = useCallback((s: SaveStatus) => setDefStatus(s), []);
  const handleWfStatus = useCallback((s: SaveStatus) => setWfStatus(s), []);
  const handleAutoStatus = useCallback((s: SaveStatus) => setAutoStatus(s), []);

  if (query.isLoading) {
    return <BuilderSkeleton />;
  }
  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={ArrowLeft}
        title="Couldn't load form"
        description={
          query.error?.code === "not_found"
            ? "This form has been deleted or never existed."
            : (query.error?.message ?? "Try refreshing.")
        }
        action={
          <Link to="/admin/forms" className="text-sm text-accent hover:underline">
            ← Back to forms
          </Link>
        }
      />
    );
  }

  const form = query.data;
  const aggregateStatus = aggregateSaveStatus(defStatus, wfStatus, autoStatus);

  return (
    <div>
      <BuilderHeader
        form={form}
        saveStatus={aggregateStatus}
        onOpenShare={() => setShareOpen(true)}
      />

      <TabsNav active={tab} onChange={onTabChange} />

      <div role="tabpanel" className="mt-4">
        {tab === "build" && (
          <FormBuilderCanvas form={form} onSaveStatusChange={handleDefStatus} />
        )}
        {tab === "automation" && (
          <AutomationConfigForm
            formId={form.id}
            automation={form.automation}
            onSaveStatusChange={handleAutoStatus}
          />
        )}
        {tab === "workflow" && (
          <WorkflowConfigForm
            formId={form.id}
            workflow={form.workflow}
            definition={form.definition}
            onSaveStatusChange={handleWfStatus}
          />
        )}
      </div>

      <ShareFormDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        slug={form.slug}
      />
    </div>
  );
}

// ============================================================================
// Header — title + status badge + save indicator + actions
// ============================================================================

function BuilderHeader({
  form,
  saveStatus,
  onOpenShare,
}: {
  form: {
    id: string;
    slug: string;
    name: string;
    status: import("@colab/shared").FormStatus;
    is_system: boolean;
  };
  saveStatus: SaveStatus;
  onOpenShare: () => void;
}) {
  const navigate = useNavigate();
  const publishMutation = usePublishForm();
  const unpublishMutation = useUnpublishForm();
  const archiveMutation = useArchiveForm();
  const deleteMutation = useDeleteForm();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const pendingStatus =
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    archiveMutation.isPending;

  function handlePublish() {
    publishMutation.mutate(form.id, {
      onSuccess: () => toast.success("Published"),
      onError: (err) => toast.error(formatApiError(err, "Failed to publish")),
    });
  }

  function handleUnpublish(label = "Unpublished") {
    unpublishMutation.mutate(form.id, {
      onSuccess: () => toast.success(label),
      onError: (err) => toast.error(formatApiError(err, "Failed to unpublish")),
    });
  }

  function handleArchive() {
    archiveMutation.mutate(form.id, {
      onSuccess: () => toast.success("Archived"),
      onError: (err) => toast.error(formatApiError(err, "Failed to archive")),
    });
  }

  function handleDelete() {
    deleteMutation.mutate(form.id, {
      onSuccess: () => {
        toast.success("Form deleted");
        navigate("/admin/forms");
      },
      onError: (err) => toast.error(formatApiError(err, "Failed to delete")),
    });
  }

  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <Link
          to="/admin/forms"
          className="mb-1.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          All forms
        </Link>
        <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight">
          {form.name}
          <FormStatusBadge status={form.status} />
          {form.is_system && (
            <span
              className="inline-flex items-center text-muted-foreground/70"
              title="System form — cannot be deleted"
              aria-label="System form"
            >
              <Lock className="h-3.5 w-3.5" />
            </span>
          )}
        </h1>
        <p className="mt-1 font-mono text-xs text-muted-foreground">/{form.slug}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <SaveIndicator state={saveStatus} />

        <Button size="sm" variant="outline" onClick={onOpenShare}>
          <Share2 className="mr-1.5 h-3.5 w-3.5" />
          Share
        </Button>

        {form.status === "published" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={pendingStatus}
            onClick={() => handleUnpublish()}
          >
            Unpublish
          </Button>
        ) : (
          <Button size="sm" disabled={pendingStatus} onClick={handlePublish}>
            Publish
          </Button>
        )}

        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" aria-label="More actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {form.status !== "archived" && (
                <DropdownMenuItem onClick={handleArchive} disabled={pendingStatus}>
                  Archive form
                </DropdownMenuItem>
              )}
              {form.status === "archived" && (
                <DropdownMenuItem
                  onClick={() => handleUnpublish("Restored to draft")}
                  disabled={pendingStatus}
                >
                  Restore to draft
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DeleteMenuItem
                isSystem={form.is_system}
                onOpenChange={setDeleteOpen}
              />
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this form?</AlertDialogTitle>
              <AlertDialogDescription>
                Existing submissions keep their snapshot but new ones can&apos;t
                be submitted. This can&apos;t be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Delete form
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function DeleteMenuItem({
  isSystem,
  onOpenChange,
}: {
  isSystem: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  if (isSystem) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuItem
              disabled
              className="text-destructive focus:text-destructive"
              onSelect={(e) => e.preventDefault()}
            >
              Delete form
            </DropdownMenuItem>
          </TooltipTrigger>
          <TooltipContent side="left">
            System form — cannot be deleted
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <AlertDialogTrigger asChild>
      <DropdownMenuItem
        className="text-destructive focus:text-destructive"
        onSelect={(e) => {
          e.preventDefault();
          onOpenChange(true);
        }}
      >
        Delete form
      </DropdownMenuItem>
    </AlertDialogTrigger>
  );
}

function SaveIndicator({ state }: { state: SaveStatus }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-status-active">
        <Check className="h-3 w-3" />
        Saved
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-destructive">
        Save failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70">
      <Cloud className="h-3 w-3" />
      Auto-save on
    </span>
  );
}

// ============================================================================
// Tabs nav — manual buttons mirroring partner-detail-page convention
// ============================================================================

function TabsNav({
  active,
  onChange,
}: {
  active: Tab;
  onChange: (next: Tab) => void;
}) {
  return (
    <div className="flex items-center gap-0.5 border-b border-border/70">
      {TABS.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`relative inline-flex h-9 items-center gap-1.5 border-b-2 px-3 text-sm font-medium transition-colors ${
              isActive
                ? "border-accent text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            role="tab"
            aria-selected={isActive}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Loading skeleton
// ============================================================================

function BuilderSkeleton() {
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-3 w-24" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      </div>
      <Skeleton className="h-9 w-full" />
      <div className="mt-4 grid gap-6 lg:grid-cols-10">
        <div className="space-y-4 lg:col-span-7">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl" />
        </div>
        <div className="lg:col-span-3">
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}
